import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Paper,
  FormControlLabel,
  Checkbox,
  Grid,
  CircularProgress,
  Container,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { createStaffMember, getPermissionsCatalog } from '../../api/users.api';
import { getStaffMembers } from '../../store/actions/users';
import Page from '../../components/Page';

export default function CreateStaffPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('invite'); // 'invite' | 'direct'

  // Permissions state from backend catalog
  const [catalogGrouped, setCatalogGrouped] = useState({});
  const [rolePermissionsMap, setRolePermissionsMap] = useState({});
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    async function fetchCatalog() {
      try {
        const res = await getPermissionsCatalog();
        if (res.ok && res.data?.data) {
          const { grouped, catalog, rolePermissions } = res.data.data;

          const finalGrouped = grouped || (catalog || []).reduce((acc, item) => {
            const groupName = item.group || 'General';
            acc[groupName] = acc[groupName] || [];
            acc[groupName].push(item);
            return acc;
          }, {});

          setCatalogGrouped(finalGrouped);
          setRolePermissionsMap(rolePermissions || {});

          const initialDefaults = rolePermissions?.support || [];
          setSelectedPermissions(
            initialDefaults.includes('*')
              ? (catalog || []).map((c) => c.id)
              : initialDefaults
          );
        }
      } catch (err) {
        console.error('Failed to load permissions catalog', err);
      } finally {
        setLoadingCatalog(false);
      }
    }
    fetchCatalog();
  }, []);

  const validationSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email address').required('Email address is required'),
    role: Yup.string()
      .oneOf(['admin', 'manager', 'developer', 'support', 'finance'], 'Invalid role')
      .required('Role is required'),
    ...(mode === 'direct' && {
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      password: Yup.string().required('Password is required').min(6, 'Password should be at least 6 characters long'),
    }),
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: 'support',
      sendWelcomeEmail: true,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);

      const roleDefaults = rolePermissionsMap[values.role] || [];
      const isWildcardRole = roleDefaults.includes('*');

      let customPermissions = [];
      let deniedPermissions = [];

      if (!isWildcardRole) {
        customPermissions = selectedPermissions.filter((p) => !roleDefaults.includes(p));
        deniedPermissions = roleDefaults.filter((p) => !selectedPermissions.includes(p));
      }

      const payload = mode === 'invite'
        ? {
            email: values.email,
            role: values.role,
            customPermissions,
            deniedPermissions,
          }
        : {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            password: values.password,
            role: values.role,
            customPermissions,
            deniedPermissions,
            sendWelcomeEmail: values.sendWelcomeEmail,
          };

      const request = await createStaffMember(payload);
      if (request.ok) {
        toast.success(mode === 'invite' ? 'Invitation sent successfully' : 'Staff successfully created');
        dispatch(getStaffMembers());
        setLoading(false);
        navigate('/dashboard/staff');
        return;
      }

      const error = request.data?.message ? request.data?.message : 'Unable to complete action';
      toast.error(error);
      setLoading(false);
    },
  });

  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    formik.setFieldValue('role', newRole);

    const defaults = rolePermissionsMap[newRole] || [];
    if (defaults.includes('*')) {
      const allIds = Object.values(catalogGrouped).flat().map((item) => item.id);
      setSelectedPermissions(allIds);
    } else {
      setSelectedPermissions(defaults);
    }
  };

  const togglePermission = (permId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permId) ? prev.filter((p) => p !== permId) : [...prev, permId]
    );
  };

  const toggleGroupPermissions = (groupItems) => {
    const groupIds = groupItems.map((i) => i.id);
    const allChecked = groupIds.every((id) => selectedPermissions.includes(id));

    if (allChecked) {
      // Deselect all in group
      setSelectedPermissions((prev) => prev.filter((id) => !groupIds.includes(id)));
    } else {
      // Select all in group
      setSelectedPermissions((prev) => Array.from(new Set([...prev, ...groupIds])));
    }
  };

  const getSubmitButtonText = () => {
    if (loading) return 'Saving...';
    if (mode === 'invite') return 'Send Invitation';
    return 'Create Staff Member';
  };

  return (
    <Page title="Add Staff Member">
      <Container maxWidth="lg">
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
          <IconButton onClick={() => navigate('/dashboard/staff')} sx={{ bgcolor: '#F4F5F7' }}>
            <ArrowBackIcon />
          </IconButton>
          <div>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1E1E2F' }}>
              Add New Staff Member
            </Typography>

          </div>
        </Stack>

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Left Column: Basic Information */}
            <Grid item xs={12} md={5}>
              <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)', p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Member Profile
                </Typography>

                <Paper 
                  variant="outlined" 
                  sx={{ 
                    mb: 3, 
                    borderRadius: '8px', 
                    overflow: 'hidden', 
                    borderColor: 'rgba(93, 95, 239, 0.2)' 
                  }}
                >
                  <Tabs 
                    value={mode} 
                    onChange={(e, val) => {
                      setMode(val);
                      formik.resetForm();
                    }} 
                    variant="fullWidth"
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{
                      background: '#F9FAFC',
                      '& .MuiTab-root': {
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        py: 1.5,
                      }
                    }}
                  >
                    <Tab label="Email Invite" value="invite" />
                    <Tab label="Direct Account" value="direct" />
                  </Tabs>
                </Paper>

                <Stack spacing={2.5}>
                  {mode === 'direct' && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        id="firstName"
                        name="firstName"
                        label="First Name"
                        value={formik.values.firstName}
                        onChange={formik.handleChange}
                        error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                        helperText={formik.touched.firstName && formik.errors.firstName}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      />

                      <TextField
                        fullWidth
                        id="lastName"
                        name="lastName"
                        label="Last Name"
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                        error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                        helperText={formik.touched.lastName && formik.errors.lastName}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      />
                    </Stack>
                  )}

                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email Address"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />

                  {mode === 'direct' && (
                    <>
                      <TextField
                        fullWidth
                        id="password"
                        name="password"
                        label="Password"
                        type="password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                        helperText={formik.touched.password && formik.errors.password}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.sendWelcomeEmail}
                            onChange={(e) => formik.setFieldValue('sendWelcomeEmail', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Send welcome login credentials by email"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem', color: '#555' } }}
                      />
                    </>
                  )}

                  <TextField
                    fullWidth
                    id="role"
                    name="role"
                    label="Base Role"
                    select
                    value={formik.values.role}
                    onChange={handleRoleChange}
                    error={formik.touched.role && Boolean(formik.errors.role)}
                    helperText={formik.touched.role && formik.errors.role}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  >
                    <MenuItem value="admin">Administrator (Full Access)</MenuItem>
                    <MenuItem value="manager">Manager</MenuItem>
                    <MenuItem value="developer">Developer</MenuItem>
                    <MenuItem value="support">Support Agent</MenuItem>
                    <MenuItem value="finance">Finance Specialist</MenuItem>
                  </TextField>
                </Stack>
              </Card>
            </Grid>

            {/* Right Column: Access Permissions Matrix */}
            <Grid item xs={12} md={7}>
              <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)', p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                  <div>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Access Permissions
                    </Typography>

                  </div>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#5D5FEF' }}>
                    {selectedPermissions.length} Active
                  </Typography>
                </Stack>

                <Divider sx={{ mb: 3 }} />

                {loadingCatalog ? (
                  <Stack alignItems="center" py={6}>
                    <CircularProgress size={32} />
                  </Stack>
                ) : (
                  <Stack spacing={2.5}>
                    {Object.entries(catalogGrouped).map(([groupName, items]) => {
                      const groupIds = items.map((i) => i.id);
                      const selectedInGroup = groupIds.filter((id) => selectedPermissions.includes(id));
                      const isAllGroupSelected = selectedInGroup.length === groupIds.length;
                      const isSomeGroupSelected = selectedInGroup.length > 0 && !isAllGroupSelected;

                      return (
                        <Paper
                          key={groupName}
                          variant="outlined"
                          sx={{ p: 2, borderRadius: '10px', borderColor: '#EAECEF' }}
                        >
                          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#5D5FEF', fontSize: '0.8rem' }}
                            >
                              {groupName} ({selectedInGroup.length}/{items.length})
                            </Typography>

                            <Tooltip title={isAllGroupSelected ? 'Deselect Group' : 'Select All in Group'}>
                              <Button
                                size="small"
                                onClick={() => toggleGroupPermissions(items)}
                                startIcon={(() => {
                                  if (isAllGroupSelected) return <CheckBoxIcon color="primary" />;
                                  if (isSomeGroupSelected) return <IndeterminateCheckBoxIcon color="primary" />;
                                  return <CheckBoxOutlineBlankIcon color="action" />;
                                })()}
                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                              >
                                {isAllGroupSelected ? 'Deselect All' : 'Select All'}
                              </Button>
                            </Tooltip>
                          </Stack>

                          <Grid container spacing={1.5}>
                            {items.map((item) => {
                              const isChecked = selectedPermissions.includes(item.id);
                              return (
                                <Grid item xs={12} sm={6} key={item.id}>
                                  <Paper
                                    variant="outlined"
                                    onClick={() => togglePermission(item.id)}
                                    sx={{
                                      p: 1.2,
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      borderColor: isChecked ? '#5D5FEF' : '#E5E7EB',
                                      backgroundColor: isChecked ? 'rgba(93, 95, 239, 0.04)' : '#FFFFFF',
                                      transition: 'all 0.15s ease-in-out',
                                      '&:hover': {
                                        borderColor: '#5D5FEF',
                                      },
                                    }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={isChecked}
                                          onChange={() => togglePermission(item.id)}
                                          color="primary"
                                        />
                                      }
                                      label={
                                        <Box sx={{ ml: 0.5 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.825rem' }}>
                                            {item.label}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, fontSize: '0.725rem' }}>
                                            {item.description}
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{ m: 0, alignItems: 'flex-start' }}
                                    />
                                  </Paper>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Card>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3, mb: 4 }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/dashboard/staff')}
                  sx={{ borderRadius: '8px', px: 4 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    borderRadius: '8px',
                    px: 5,
                    fontWeight: 700,
                    background: '#5D5FEF',
                    boxShadow: 'none',
                    '&:hover': { background: '#4B4DCC' },
                  }}
                >
                  {getSubmitButtonText()}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </Container>
    </Page>
  );
}
